import { ClientRepresentationStatusEnum } from '@sr-sdks/api-sdk-axios';
import { Combobox, Skeleton, useCombobox } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CLIENTS_DEFAULT_PARAMS,
  queryOptionsGetClientsPaginated,
} from '../../../api/workspaces/clientApi';
import { AppSearchInput } from '../../../components/AppSearchInput';
import { ClientCard } from '../../../components/ClientCard/ClientCard';
import { DownloadCsvButton } from '../../../components/DownloadCsvButton';
import ErrorPage from '../../../components/ErrorPage';
import { ListToolbar } from '../../../components/ListToolbar';
import { MSR3Empty } from '../../../components/MSR3Empty';
import { PageHeader } from '../../../components/PageHeader';
import { PageHeaderActions } from '../../../components/PageHeaderActionsSlot';
import { LoadingFallback } from '../../../components/QueryResults/LoadingFallback';
import { SingleQueryResults } from '../../../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../../../hooks/useActiveWorkspace';
import { useDebouncedUrlParam } from '../../../hooks/useDebouncedUrlParam';
import { usePaginationBounds } from '../../../hooks/usePaginationBounds';
import { useResetPageOnSearchChange } from '../../../hooks/useResetPageOnSearchChange';
import { MSR3BaseLayout } from '../../../layout/MSR3BaseLayout';
import { isCsvExportDisabled } from '../../../utils/csv/csvExport';
import { workspaceTypeIsAgent } from '../../../utils/helpers/common';
import { clientsCsvColumns, fetchClientsCsvPage } from './clientsCsvSpec';

const PAGE_PARAM = 'clientsPage';
const SEARCH_PARAM = 'clientsSearch';
const STATUS_PARAM = 'clientsStatus';
const PAGE_SIZE = CLIENTS_DEFAULT_PARAMS.pageSize;

// Declined is omitted: the API never returns declined clients to external users.
const statusFilterOptions = [
  { label: 'Confirmed', value: ClientRepresentationStatusEnum.Confirmed },
  { label: 'Unconfirmed', value: ClientRepresentationStatusEnum.Unconfirmed },
];

const VALID_STATUS_VALUES = new Set<string>(
  statusFilterOptions.map((option) => option.value),
);

const isStatusFilterValue = (
  value: string,
): value is ClientRepresentationStatusEnum => VALID_STATUS_VALUES.has(value);

const parseStatusParam = (param: string) =>
  param.split(',').filter(isStatusFilterValue);

/**
 * Shows only the fields the clients endpoint returns today. Title counts, last
 * payment and service badges need backend work and are separate stories.
 */
const Content = () => {
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;
  const basePath = `/workspace/${workspaceId ?? ''}`;

  // Search, filter and page live in the URL so they survive back-navigation.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const statuses = useMemo(
    () => parseStatusParam(searchParams.get(STATUS_PARAM) ?? ''),
    [searchParams],
  );
  const page = Math.max(1, Number(searchParams.get(PAGE_PARAM)) || 1);

  const setParam = useCallback(
    (key: string, value: string) =>
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          if (value) updated.set(key, value);
          else updated.delete(key);

          return updated;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (next: number) => setParam(PAGE_PARAM, String(next)),
    [setParam],
  );

  // Sorted and de-duplicated so selection order can't produce a different URL,
  // and therefore a different query cache entry, for the same filter.
  const setStatusFilters = useCallback(
    (values: string[]) =>
      setParam(
        STATUS_PARAM,
        [...new Set(values.filter(isStatusFilterValue))].sort().join(','),
      ),
    [setParam],
  );

  const statusFilterCombobox = useCombobox({
    hasSearch: false,
    options: statusFilterOptions,
    selected: statuses,
    setSelected: setStatusFilters,
  });

  const setSearch = useCallback(
    (value: string) => setParam(SEARCH_PARAM, value),
    [setParam],
  );

  useResetPageOnSearchChange(
    `${search}|${statuses.join(',')}`,
    PAGE_PARAM,
    handlePageChange,
  );

  // The filters and sort alone: the CSV fetcher supplies its own page and
  // pageSize, and a sort tiebreaker, so the file lists the rows in this order.
  const clientsParams = {
    representationStatuses: statuses.length ? statuses : undefined,
    // searchTerm, not clientName: only this param also matches the client
    // reference id and short id, which the placeholder promises.
    searchTerm: search || undefined,
    sortBy: 'clientName:asc',
    workspaceId,
  };

  const clientsQuery = useQuery({
    ...queryOptionsGetClientsPaginated({
      ...clientsParams,
      page,
      pageSize: PAGE_SIZE,
    }),
    // The workspace has already resolved by the time this renders; the guard
    // keeps the request honest if that ever stops being true.
    enabled: Boolean(workspaceId),
  });

  const {
    input: searchInput,
    isSearchFetching = false,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(search, setSearch, {
    queryStatus: {
      isFetching: clientsQuery.isFetching,
      isPending: clientsQuery.isPending,
    },
  });

  const totalPages = clientsQuery.data?.meta?.totalPages ?? 0;
  const totalItems = clientsQuery.data?.meta?.totalItems ?? 0;

  const { clampedPage, isPageOutOfRange } = usePaginationBounds({
    page,
    setPage: handlePageChange,
    totalPages,
  });

  const hasFilters = Boolean(search || statuses.length);

  const listSkeleton = (
    <div className="space-y-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-[108px] w-full rounded-lg" />
      ))}
    </div>
  );

  // Deliberately not disabled on isError: keepPreviousData keeps the stale list
  // on screen, so disabling here would strip every way to retry.
  const areControlsDisabled = clientsQuery.isPending || !workspaceId;

  return (
    <>
      <PageHeaderActions>
        <DownloadCsvButton
          label="Clients"
          columns={clientsCsvColumns}
          fetchPage={(exportPage, pageSize, signal) =>
            fetchClientsCsvPage(clientsParams, exportPage, pageSize, signal)
          }
          fileLabel="clients"
          isDisabled={isCsvExportDisabled(clientsQuery, totalItems)}
          rowCount={totalItems}
        />
      </PageHeaderActions>

      <div className="flex flex-wrap items-center gap-3">
        <AppSearchInput
          classes={{ wrapper: 'sm:w-64' }}
          disabled={areControlsDisabled}
          isFetching={isSearchFetching}
          placeholder="Search by client name or ID"
          value={searchInput}
          onChange={setSearchInput}
        />

        <Combobox
          classes={{ dropdownTrigger: 'sm:w-48' }}
          disabled={areControlsDisabled}
          multipleSelectedPlaceholder="Statuses"
          placeholder="Filter Status"
          store={statusFilterCombobox}
        />
      </div>

      <ListToolbar
        currentPage={clampedPage}
        disabled={areControlsDisabled}
        pageSize={PAGE_SIZE}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <SingleQueryResults
        loadingFallback={listSkeleton}
        // A disabled query is pending-but-idle, which would otherwise paint
        // a blank body rather than the skeleton.
        notFetched={listSkeleton}
        query={clientsQuery}
      >
        {({ items: clients }) =>
          clients.length === 0 ? (
            isPageOutOfRange ? (
              listSkeleton
            ) : (
              <MSR3Empty
                {...MSR3Empty.Props[hasFilters ? 'no-result' : 'client']}
              />
            )
          ) : (
            <div className="space-y-4">
              {clients.map((client) => (
                <ClientCard
                  key={client._id}
                  basePath={basePath}
                  client={client}
                />
              ))}
            </div>
          )
        }
      </SingleQueryResults>
    </>
  );
};

export const ClientsV3 = () => {
  const [activeWorkspace, activeWorkspaceQuery] = useActiveWorkspace();

  // Placeholder data is the *previous* workspace (keepPreviousData), so success
  // alone would decide the guards below against whichever workspace the user
  // came from rather than the one in the URL.
  const isWorkspaceResolved =
    Boolean(activeWorkspace) && !activeWorkspaceQuery.isPlaceholderData;

  // Only a workspace that never resolved is an error: keeping the cached one on
  // screen through a failed background refetch is what SingleQueryResults does
  // for every sibling guard, and tearing the page down would cost the user their
  // search and filters for a transient blip.
  if (activeWorkspaceQuery.isError && !isWorkspaceResolved)
    return <ErrorPage genericError />;

  // The header waits for the workspace: painting it first would flash the page
  // title at a user who is about to be denied.
  if (!isWorkspaceResolved) return <LoadingFallback />;

  // Clients is agent-only. Not-found rather than not-authorised, the same answer
  // the other workspace-shape guards give (RequireCurrentMember,
  // RequireAddonEnabled), so the page's existence is not confirmed either.
  if (!workspaceTypeIsAgent(activeWorkspace)) return <ErrorPage notFound />;

  return (
    <MSR3BaseLayout
      classes={{ childrenWrapper: 'space-y-4' }}
      pageHeader={<PageHeader title="Clients" />}
    >
      <Content />
    </MSR3BaseLayout>
  );
};
